import {Component} from '@angular/core';
import {Alert, AlertController, NavController, NavParams, Platform, Refresher, ViewController} from 'ionic-angular';
import {FilterItem} from "../../models/FilterItem";
import {
  CYCLE_COUNT_DEFAULT_COLUMNS_LENGTH,
  CYCLE_COUNT_DEFAULT_EXTRA_COLUMNS_LENGTH,
  NAV_PARAM_SELECTED_DEPARTMENT,
  NAV_PARAM_SELECTED_ZONE
} from "../../app/app.constants";
import {VizixService} from "../../providers/vizix.service";
import {DepartmentsZones, ZoneType} from '../../models/zoneDeptRelation';
import {TableScriptReportResponse} from '../../models/TableScriptReportResponse';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/finally';
import {MapPipe} from '../../pipes/map/map';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'page-cycle-count-filters-modal',
  templateUrl: 'cycle-count-filters-modal.html',
})
export class CycleCountFiltersModal {

  reportFilterLabelList: Array<string> = [];
  filtersList: Array<FilterItem> = [];
  columns: Array<string> = [];
  reportColumns: Map<string, number> = new Map();
  selectedZone: ZoneType;
  selectedDepartment: DepartmentsZones;
  filteredMapList: Map<string, Map<string, Array<number>>> = new Map();
  filteredMapExtraList: Map<string, Map<string, Array<number>>> = new Map();
  remainingItemsList: Array<Array<string>>;
  lastCycleCount: Array<Array<string>>;
  cycleCountReportResponse: TableScriptReportResponse;
  loading: boolean = false;
  loaded: boolean = false;
  firstLoad: boolean = true;
  isAndroid: boolean;
  alert: Alert = null;

  filterSelections: Array<{ label: string, value: Array<number> }> = [];

  public resultFilteredArray: Array<Array<number>> = [];
  public resultFilteredExtraArray: Array<Array<number>> = [];
  public defaultColumnsLength: number = CYCLE_COUNT_DEFAULT_COLUMNS_LENGTH;
  public defaultExtraColumnsLength: number = CYCLE_COUNT_DEFAULT_EXTRA_COLUMNS_LENGTH;

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public viewCtrl: ViewController,
              public alertCtrl: AlertController,
              public translate: TranslateService,
              private mapPipe: MapPipe,
              private platform: Platform,
              public vizixService: VizixService) {
    this.initVariables();
  }

  ionViewDidLoad() {
    this.getFilterColumnsData();
  }

  ionViewDidEnter() {
    this.initBackButton();
  }

  initVariables() {
    this.isAndroid = this.platform.is('android');
    this.selectedZone = this.navParams.get(NAV_PARAM_SELECTED_ZONE);
    this.selectedDepartment = this.navParams.get(NAV_PARAM_SELECTED_DEPARTMENT);
  }

  // Alert that shows when Vizix is Still Processing
  showServerProcessingAlert() {
    this.alert = this.alertCtrl.create({
      title: this.translate.instant('cycle_count.alerts.server_processing.title'),
      message: this.translate.instant('cycle_count.alerts.server_processing.message'),
      enableBackdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('cycle_count.alerts.server_processing.ok'),
          handler: () => {
            this.viewCtrl.dismiss(); // We call this recursively because the alert is destroyed on dismiss
          }
        }
      ]
    });
    this.alert.onDidDismiss(() => this.alert = null);
    this.alert.present();
  }

  filter(position: number, filterLabel: string, filterValue: string) {
    if (position >= 0) {
      // The list of positions of the current filter
      const filterItem = this.filteredMapList.get(filterLabel);
      const selectedPositionList = filterItem ? filterItem.get(filterValue) : [];

      const filterExtraItem = this.filteredMapExtraList.get(filterLabel);
      const selectedPositionExtraList = filterExtraItem ? filterExtraItem.get(filterValue) : [];

      // The filtering function (intersection)
      const filterFunction = (position) => {
        return selectedPositionList.indexOf(position) > -1;
      };

      // The filtering function (intersection)
      const filterExtraFunction = (position) => {
        if (selectedPositionExtraList) {
          return selectedPositionExtraList.indexOf(position) > -1;
        } else {
          return false;
        }
      };

      // Save the current selected filter value
      this.filterSelections[position].label = filterValue;

      // DATA ARRAY

      // When selecting from the last filter visible
      if (position == this.resultFilteredArray.length) {
        if (position == 0) {
          this.resultFilteredArray.push(selectedPositionList);
        } else {
          this.resultFilteredArray.push(
            this.resultFilteredArray[position - 1].filter(filterFunction)
          );
        }
      }

      // When selecting from a filter that is not the last one
      else if (position < this.resultFilteredArray.length) {
        this.resultFilteredArray[position] = position > 0 ?
          this.resultFilteredArray[position - 1].filter(filterFunction) :
          selectedPositionList;

        // Now we clear the filters of index > position
        this.resultFilteredArray = this.resultFilteredArray.slice(0, position + 1);
        // Also clear the selections of index > position
        this.filterSelections = this.filterSelections.map((selection, index) => {
          return index > position ? {label: '', value: []} : selection;
        });
      }

      // EXTRAS ARRAY

      // When selecting from the last filter visible
      if (position == this.resultFilteredExtraArray.length) {
        if (position == 0) {
          this.resultFilteredExtraArray.push(selectedPositionList);
        } else {
          this.resultFilteredExtraArray.push(
            this.resultFilteredExtraArray[position - 1].filter(filterExtraFunction)
          );
        }
      }

      // When selecting from a filter that is not the last one
      else if (position < this.resultFilteredExtraArray.length) {
        this.resultFilteredExtraArray[position] = position > 0 ?
          this.resultFilteredExtraArray[position - 1].filter(filterExtraFunction) :
          selectedPositionList;

        // Now we clear the filters of index > position
        this.resultFilteredExtraArray = this.resultFilteredExtraArray.slice(0, position + 1);
      }
    }
  }

  /**
   * Get report definition columns
   */
  getColumnsFromReport(): Observable<Array<{ displayOrder: number, label: string }>> {
    const observable = this.vizixService.getCycleCountFilterColumnsDefinition();
    observable.subscribe((response: Array<{ displayOrder: number, label: string }>) => {
      // Order the columns with the response
      this.reportColumns.clear();
      for (let column of response) {
        this.reportColumns.set(column.label, column.displayOrder);
      }
    });
    return observable;
  }

  /**
   * Get report definition columns
   */
  orderColumns(): void {
    this.columns.sort((col1, col2) => {
        const displayOrderCol1 = this.reportColumns.get(col1);
        const displayOrderCol2 = this.reportColumns.get(col2);
        if (displayOrderCol1 && displayOrderCol2) {
          return displayOrderCol1 > displayOrderCol2 ? 1 : -1;
        } else {
          return 0;
        }
      }
    );
  }

  /**
   * Fill selectables with data
   */
  getFilterColumnsData(refresher?: Refresher): void {

    this.clearFilters(true);

    this.loading = true;
    let reportBody = {};
    reportBody[this.vizixService.configurationApp.CycleCountConfiguration.FilterLabelZoneType.value] = String(this.selectedZone.idZone);
    reportBody[this.vizixService.configurationApp.CycleCountConfiguration.FilterLabelStoreID.value] = this.vizixService.getStoreId();

    if (!this.selectedDepartment.ALL) {
      reportBody[this.vizixService.configurationApp.CycleCountConfiguration.FilterLabelDepartment.value] = this.selectedDepartment.DeptName;
    }

    /**
     * Map filter columns data
     */
    const mapFilterColumnsData = (response: TableScriptReportResponse): void => {

      if (!response.isProcessing) {
        this.cycleCountReportResponse = response;

        const columns = this.cycleCountReportResponse.columnNames;
        const extraColumns = this.cycleCountReportResponse.extraColumnNames;

        // Data
        this.cycleCountReportResponse.data.forEach((row: Array<string>, index: number) => {
          // If has filters
          if (row.length > this.defaultColumnsLength) { // The number 7 comes from the amount of default columns before the filterable columns
            for (let i = this.defaultColumnsLength; i < columns.length; i++) {
              if (this.filteredMapList.has(columns[i])) {
                let filteredRow = this.filteredMapList.get(columns[i]);
                if (filteredRow.has(row[i])) {
                  filteredRow.get(row[i]).push(index);
                } else {
                  let list: Array<number> = [];
                  list.push(index);
                  filteredRow.set(row[i], list);
                }
              } else {
                let map = new Map<string, Array<number>>();
                map.set(row[i], [index]);
                this.filteredMapList.set(columns[i], map);
              }
            }
          }
        });

        this.columns = [];

        this.filteredMapList.forEach((value, key) => {
          this.filterSelections.push({label: '', value: []});
          this.columns.push(key);
        });

        // Extra Data
        this.cycleCountReportResponse.extra.forEach((row: Array<string>, index: number) => {
          // If has filters
          if (row.length > this.defaultExtraColumnsLength) { // The number 2 comes from the amount of default extra extraColumns before the filterable extraColumns
            for (let i = this.defaultExtraColumnsLength; i < extraColumns.length; i++) {
              if (this.filteredMapExtraList.has(extraColumns[i])) {
                let filteredRow = this.filteredMapExtraList.get(extraColumns[i]);
                if (filteredRow.has(row[i])) {
                  filteredRow.get(row[i]).push(index);
                } else {
                  let list: Array<number> = [];
                  list.push(index);
                  filteredRow.set(row[i], list);
                }
              } else {
                let map = new Map<string, Array<number>>();
                map.set(row[i], [index]);
                this.filteredMapExtraList.set(extraColumns[i], map);
              }
            }
          }
        });
        this.orderColumns();
      } else {
        this.showServerProcessingAlert();
      }

      this.loaded = true;
      this.hideLoading(refresher);
    };

    this.getColumnsFromReport().subscribe(() => {
      this.vizixService.VizixPostReportExecutionCycleCount(reportBody)
        .subscribe(mapFilterColumnsData, () => this.hideLoading(refresher));
    }, (error) => {
      console.log(error);
      this.loaded = true;
      this.hideLoading(refresher);
    });

  }

  returnOptions(key: string, index: number) {
    const toSearchPositionsArray = index > 0 ? this.resultFilteredArray[index - 1] : null;
    const options = this.mapPipe.transform(this.filteredMapList, key, toSearchPositionsArray);
    return options;
  }

  selectedFilter(selection: { label: string, value: Array<number> }, key: string, index: number) {
    this.filter(index, key, selection.label);
    const nextFilter = index + 1;
    if (nextFilter <= this.filterSelections.length) {
      this.filterSelections[nextFilter] = {label: '', value: []};
    }
  }

  cancel() {
    this.viewCtrl.dismiss();
  }

  accept() {
    // First we copy the original extra (LastCycleCounts)
    this.cycleCountReportResponse.extraOriginal = this.cycleCountReportResponse.extra.slice(0);
    this.filterReportResponse();

    let selectedFilterList: { label: string, value: string }[] = [];
    this.columns.forEach((column, index) => {
      if (index < this.resultFilteredArray.length) {
        selectedFilterList.push({label: column, value: this.filterSelections[index].label});
      }
    });

    this.viewCtrl.dismiss({response: this.cycleCountReportResponse, selectedFilters: selectedFilterList});
  }

  filterReportResponse() {
    // Filter response.data
    const lastFilterArray = this.resultFilteredArray[this.resultFilteredArray.length - 1];
    this.cycleCountReportResponse.data = this.filterArrayPositions(this.cycleCountReportResponse.data, lastFilterArray);
    this.cycleCountReportResponse.totalRows = this.cycleCountReportResponse.data.length;

    // Update the Group Qty to match the filtered length
    let groupQty = 0;

    for (let row of this.cycleCountReportResponse.data) {
      groupQty += Number(row[4]);
    }

    this.cycleCountReportResponse.data = this.cycleCountReportResponse.data.map((row) => {
      let newRow: Array<string> = row.slice(0);
      newRow[1] = String(groupQty);
      return newRow;
    });

    // Filter response.extra
    const lastFilterExtraArray = this.resultFilteredExtraArray[this.resultFilteredExtraArray.length - 1];
    this.cycleCountReportResponse.extra = this.filterArrayPositions(this.cycleCountReportResponse.extra, lastFilterExtraArray);
  }

  filterArrayPositions(arrayToFilter: Array<any>, positionsArray: Array<any>) {
    return arrayToFilter.filter((row, index) => {
      return positionsArray.indexOf(index) > -1;
    });
  }

  private hideLoading(refresher?: Refresher) {
    this.firstLoad = false;
    this.loading = false;
    if (refresher instanceof Refresher) {
      refresher.complete();
    }
  }

  clearFilters(skipAlert ?: boolean) {
    const clearFiltersFunction = () => {
      this.resultFilteredExtraArray = [];
      this.resultFilteredArray = [];
      this.filterSelections = this.filterSelections.map(() => {
        return {label: '', value: []}
      });
    };

    if (!skipAlert) {
      this.alert = this.alertCtrl.create({
        title: this.translate.instant('cycle_count.alerts.confirm_clear_filters.title'),
        message: this.translate.instant('cycle_count.alerts.confirm_clear_filters.message'),
        buttons: [
          {
            text: this.translate.instant('cycle_count.alerts.confirm_clear_filters.cancel'),
            role: 'cancel',
            handler: () => null
          },
          {
            text: this.translate.instant('cycle_count.alerts.confirm_clear_filters.confirm'),
            handler: clearFiltersFunction
          }
        ]
      });
      this.alert.present();
      this.alert.onDidDismiss(() => this.alert = null);
    } else {
      clearFiltersFunction();
    }
  }

  initBackButton() {
    this.platform.registerBackButtonAction(() => {
      if (this.alert instanceof Alert) {
        this.alert.dismiss();
      } else {
        this.viewCtrl.dismiss();
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/cycle-count/cycle-count.filters.modal.ts